import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@nexdel/core/auth';
import { BrandLogo } from '@nexdel/ui/layout/BrandLogo';
import { MfaSetup } from '@nexdel/ui/layout/MfaSetup';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type LoginForm = z.infer<typeof loginSchema>;

const MFA_ENFORCED = (import.meta as { env: Record<string, string> }).env.VITE_MFA_ENFORCED === 'true';

export default function Login() {
  const { t, i18n } = useTranslation('auth');
  const { signIn, firebaseUser, mfaChallengePending, completeMfaChallenge, cancelMfaChallenge, needsMfaEnrollment, signOut, wrongApp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const [serverError, setServerError] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [enrolledLocally, setEnrolledLocally] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // A signed-in password user who must enroll a second factor stays on this screen (branded),
  // rather than being redirected into the app. MfaGate bounces them here when enforcement is on.
  const showEnrollment = !!firebaseUser && MFA_ENFORCED && needsMfaEnrollment;

  if (firebaseUser && !showEnrollment) {
    navigate(from, { replace: true });
    return null;
  }

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      const res = await signIn(data.email, data.password);
      if (res.mfaRequired) return; // the TOTP challenge below takes over
      // Signed in — the render guard redirects into the app, or shows enrollment if required.
    } catch {
      setServerError(t('login.invalidCredentials'));
    }
  };

  const onVerifyMfa = async (e: FormEvent) => {
    e.preventDefault();
    setMfaError(null);
    setMfaVerifying(true);
    try {
      await completeMfaChallenge(mfaCode.trim());
      // Signed in — the render guard redirects into the app.
    } catch {
      setMfaError(t('login.mfaInvalidCode'));
    } finally {
      setMfaVerifying(false);
    }
  };

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const toggleLang = () => i18n.changeLanguage(currentLang === 'fr' ? 'en' : 'fr');

  return (
    <div className="min-h-screen bg-[#081208] font-sans antialiased flex text-white/90">
      {/* Left two-thirds — atmospheric branding */}
      <div className="hidden md:flex flex-col flex-[2] relative overflow-hidden">
        <iframe
          src="https://my.spline.design/trails-e13WCmJCVeHdNBlOEyk0xzug/"
          frameBorder={0}
          className="absolute inset-0 w-full h-full blur-sm"
        />

        <div className="relative z-10 flex flex-col justify-center items-center h-full p-12 text-center gap-8">
          {/* Logo */}
          <BrandLogo className="h-28 w-auto" />

          {/* Tagline */}
          <div className="max-w-lg">
            <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-tight mb-6 whitespace-pre-line">
              {t('login.supplierHeroTitle')}
            </h1>
            <p className="text-white/60 text-lg leading-relaxed mb-8">
              {t('login.supplierHeroSubtitle')}
            </p>
            <ul className="space-y-3">
              {(['supplierFeature1', 'supplierFeature2', 'supplierFeature3'] as const).map((key) => (
                <li key={key} className="flex items-center justify-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-white/50 text-base leading-relaxed">{t(`login.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right third — login form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-12 relative">
        {/* Language toggle */}
        <button
          type="button"
          onClick={toggleLang}
          className="absolute top-6 right-6 text-xs text-white/40 hover:text-white/70 transition-colors uppercase tracking-widest"
        >
          {currentLang === 'fr' ? 'EN' : 'FR'}
        </button>

        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-2xl font-semibold text-white mb-1">
            {showEnrollment
              ? (enrolledLocally ? t('mfaEnroll.doneTitle') : t('mfaEnroll.requiredTitle'))
              : t('login.title')}
          </h2>
          <p className="text-white/40 text-sm mb-8">
            {showEnrollment
              ? (enrolledLocally ? t('mfaEnroll.doneDescription') : t('mfaEnroll.requiredDescription'))
              : t('login.subtitle')}
          </p>

          {wrongApp && (
            <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
              {t('wrongApp.supplierMessage')}
            </div>
          )}

          {serverError && (
            <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
              {serverError}
            </div>
          )}

          {showEnrollment ? (
            enrolledLocally ? (
              <button
                type="button"
                onClick={() => signOut()}
                className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors"
              >
                {t('mfaEnroll.signInAgain')}
              </button>
            ) : (
              <MfaSetup tone="login" onEnrolled={() => setEnrolledLocally(true)} />
            )
          ) : mfaChallengePending ? (
            <form onSubmit={onVerifyMfa} className="space-y-4">
              <p className="text-white/50 text-sm">{t('login.mfaPrompt')}</p>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                  {t('login.mfaCode')}
                </label>
                <input
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors text-center tracking-[0.3em]"
                />
                {mfaError && <p className="mt-1 text-xs text-red-400">{mfaError}</p>}
              </div>
              <button
                type="submit"
                disabled={mfaVerifying}
                className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors mt-2"
              >
                {mfaVerifying ? '...' : t('login.mfaVerify')}
              </button>
              <button
                type="button"
                onClick={() => { cancelMfaChallenge(); setMfaCode(''); setMfaError(null); }}
                className="w-full text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                {t('login.mfaCancel')}
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                {t('login.email')}
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                {t('login.password')}
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                placeholder={t('login.passwordPlaceholder')}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors mt-2"
            >
              {isSubmitting ? '...' : t('login.submit')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
